import { useInfiniteQuery } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { useCallback } from "react";
import { twJoin } from "tailwind-merge";

import type { ResidentNotificationDto } from "@/api/types";
import { ActivityDot } from "@/components/ActivityDot/ActivityDot";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Box } from "@/design-system/Box";
import { buttonVariants } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Link } from "@/design-system/Link";
import { twResolve } from "@/helpers/tw-resolve";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import { ResidentNotificationIcon } from "@/modules/resident-notifications/components/ResidentNotificationIcon";
import { getResidentNotificationLink } from "@/modules/resident-notifications/getResidentNotificationLink";
import { sanitizeNotificationHtml } from "@/modules/resident-notifications/sanitizeNotificationHtml";
import { residentNotificationMutations } from "@/queries/notifications/mutations";
import { useNotificationQueries } from "@/queries/notifications/queryOptions";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

const query = {} satisfies ApiQueryParams<"getResidentNotificationsV3">;

/** Renders paginated resident notifications and optimistically marks opened items as read. */
export function ResidentNotificationsPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const slug = useSlug();
  const { t } = useTranslation();
  const notificationQueries = useNotificationQueries();
  const { data, isPending, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    notificationQueries.residentListInfinite(query),
  );
  const markReadMutation = residentNotificationMutations.useMarkNotificationAsRead(query);
  const allItems = data?.pages.flatMap((page) => page.notifications.items) ?? [];
  const sevenDaysAgo = addDays(clockDate, -7);
  const itemsFromThisWeek = allItems.filter((item) => new Date(item.posted) > sevenDaysAgo);
  const itemsFromBefore = allItems.slice(itemsFromThisWeek.length);
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingNextPage && hasNextPage) {
        void fetchNextPage();
      }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]),
  });

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.resident-notifications.title.tab")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.resident-notifications.title"),
            },
          ]}
        />
      }
    >
      <ResidentNotificationList
        isPending={isPending}
        allItems={allItems}
        itemsFromThisWeek={itemsFromThisWeek}
        itemsFromBefore={itemsFromBefore}
        slug={slug}
        onMarkAsRead={(id) => markReadMutation.mutate(id)}
      />
      {hasNextPage && <CollectionLoadingState ref={ref} />}
    </DocumentPage>
  );
}

function ResidentNotificationList({
  isPending,
  allItems,
  itemsFromThisWeek,
  itemsFromBefore,
  slug,
  onMarkAsRead,
}: {
  isPending: boolean;
  allItems: ResidentNotificationDto[];
  itemsFromThisWeek: ResidentNotificationDto[];
  itemsFromBefore: ResidentNotificationDto[];
  slug: string;
  onMarkAsRead: (id: string) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  if (isPending) {
    return <FullSizeLoader withPadding />;
  }

  if (allItems.length === 0) {
    return (
      <Card as="section" density="flush">
        <Box padding="xl">
          <h2 className="text-heading-3 text-text-secondary">{t("page.resident-notifications.empty.title")}</h2>
          <p className="text-heading-4 text-text-secondary">{t("page.resident-notifications.empty.description")}</p>
        </Box>
      </Card>
    );
  }

  return (
    <>
      {itemsFromThisWeek.length > 0 && (
        <section>
          <h2 className="rounded-t-lg bg-background-default py-space-md pl-space-xl text-body-emphasis">
            {t("page.resident-notifications.this-week")}
          </h2>
          {itemsFromThisWeek.map((notification) => (
            <ResidentNotificationItem
              key={notification.id}
              notification={notification}
              slug={slug}
              onMarkAsRead={() => onMarkAsRead(notification.id)}
            />
          ))}
        </section>
      )}
      {itemsFromBefore.length > 0 && (
        <section>
          <h2
            className={twJoin(
              "bg-background-default py-space-md pl-space-xl text-body-emphasis",
              itemsFromThisWeek.length === 0 && "rounded-t-lg",
            )}
          >
            {t("page.resident-notifications.before-this-week")}
          </h2>
          {itemsFromBefore.map((notification) => (
            <ResidentNotificationItem
              key={notification.id}
              notification={notification}
              slug={slug}
              onMarkAsRead={() => onMarkAsRead(notification.id)}
            />
          ))}
        </section>
      )}
    </>
  );
}

function ResidentNotificationItem({
  notification,
  slug,
  onMarkAsRead,
}: {
  notification: ResidentNotificationDto;
  slug: string;
  onMarkAsRead: () => void;
}): React.ReactNode {
  const link = getResidentNotificationLink(notification, slug);
  const sanitizedContent = sanitizeNotificationHtml(notification.content);
  const content = (
    <>
      <span className="flex w-icon-inline shrink-0">{!notification.read && <ActivityDot />}</span>
      <span className="relative shrink-0">
        <UserAvatar image={notification.poster.avatar} />
        <span className="absolute right-0 bottom-0 flex size-icon-prominent items-center justify-center rounded-full bg-background-brand text-text-on-brand">
          <ResidentNotificationIcon type={notification.notificationType} />
        </span>
      </span>
      <span className="flex min-w-0 flex-col items-start gap-space-xs">
        <span
          className="line-clamp-2 text-left text-caption whitespace-pre-wrap text-text-primary group-hocus:underline [&>b]:text-caption-emphasis"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
        <span className="text-overline text-text-secondary">
          <FormattedDate format="datetimeRelative" date={notification.posted} />
        </span>
      </span>
    </>
  );
  const className = twJoin(
    "group flex w-full items-center gap-space-sm p-card-compact text-left",
    notification.read ? "bg-background-default even:bg-background-secondary" : "bg-background-brand-light",
  );
  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead();
    }
  };
  const linkedContent = <span className={className}>{content}</span>;

  if (link?.external) {
    return (
      <Link href={link.href} external target="_blank" fullWidth tone="default" underline="none" onClick={handleClick}>
        {linkedContent}
      </Link>
    );
  }

  if (link) {
    return (
      <Link href={link.href} fullWidth tone="default" underline="none" onClick={handleClick}>
        {linkedContent}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={twResolve(buttonVariants({ variant: "subtle", fullWidth: true }), className)}
      onClick={handleClick}
    >
      {content}
    </button>
  );
}
